import React, { useState } from "react";
import { ChevronDown, ChevronRight, Building2 } from "lucide-react";
import { formatLongDate } from "../../lib/format";

/**
 * Nested list: Category → Company → Individual contacts.
 * Used inside the Centers of Influence tab.
 */
export default function GroupedByCompany({ items, categories, catColor, onClick, type }) {
  const [openCats, setOpenCats] = useState(() => new Set(categories.map((c) => c.name)));
  const [openCompanies, setOpenCompanies] = useState(() => new Set());

  const toggleSet = (setter) => (key) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleCat = toggleSet(setOpenCats);
  const toggleCompany = toggleSet(setOpenCompanies);

  // Bucket contacts: category → company → contacts
  const byCat = {};
  const uncategorized = [];
  for (const it of items) {
    const cat = it.category;
    if (!cat) {
      uncategorized.push(it);
      continue;
    }
    if (!byCat[cat]) byCat[cat] = {};
    const co = (it.company || "(No Company)").trim() || "(No Company)";
    if (!byCat[cat][co]) byCat[cat][co] = [];
    byCat[cat][co].push(it);
  }

  const catList = categories.filter((c) => byCat[c.name]);
  // Any categories that exist on contacts but aren't in the ordered categories collection
  const extraCats = Object.keys(byCat)
    .filter((k) => !catList.find((c) => c.name === k))
    .map((k) => ({ id: `x-${k}`, name: k, color: "#707070" }));

  return (
    <div className="space-y-3">
      {[...catList, ...extraCats].map((c) => {
        const companies = byCat[c.name] || {};
        const companyNames = Object.keys(companies).sort((a, b) => a.localeCompare(b));
        const totalContacts = Object.values(companies).reduce((n, arr) => n + arr.length, 0);
        const isCatOpen = openCats.has(c.name);
        return (
          <div
            key={c.id}
            className="bg-white border border-[var(--rw-border)] rounded-[4px]"
            data-testid={`grp-cat-${c.name}`}
          >
            <button
              type="button"
              onClick={() => toggleCat(c.name)}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--rw-surface-2)] text-left"
            >
              {isCatOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: catColor?.[c.name] || c.color || "#707070" }}
              />
              <span className="font-display text-base tracking-wide">
                {c.name}
              </span>
              <span className="text-[10px] uppercase tracking-[0.08em] text-neutral-500 ml-1">
                {companyNames.length} {companyNames.length === 1 ? "company" : "companies"} · {totalContacts}{" "}
                {totalContacts === 1 ? "contact" : "contacts"}
              </span>
            </button>
            {isCatOpen && (
              <div className="border-t border-[var(--rw-border)] divide-y divide-[var(--rw-border)]">
                {companyNames.map((co) => {
                  const key = `${c.name}::${co}`;
                  const isOpen = openCompanies.has(key);
                  const contacts = companies[co];
                  return (
                    <div key={key} data-testid={`grp-company-${co}`}>
                      <button
                        type="button"
                        onClick={() => toggleCompany(key)}
                        className="w-full flex items-center gap-2 px-6 py-2 hover:bg-[var(--rw-surface-2)] text-left"
                      >
                        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        <Building2 size={12} className="text-neutral-400" />
                        <span className="font-semibold text-sm">{co}</span>
                        <span className="text-xs text-neutral-500 ml-1">
                          · {contacts.length}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="pl-14 pr-4 pb-2">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-[10px] uppercase tracking-[0.08em] text-neutral-500 font-semibold">
                                <th className="py-1 pr-3">Name</th>
                                <th className="py-1 pr-3">Email</th>
                                <th className="py-1 pr-3">Phone</th>
                                <th className="py-1 pr-3">Notes</th>
                                <th className="py-1 pr-3">Last Contact</th>
                              </tr>
                            </thead>
                            <tbody>
                              {contacts
                                .slice()
                                .sort((a, b) =>
                                  (a.last_name || "").localeCompare(b.last_name || "")
                                )
                                .map((it) => (
                                  <tr
                                    key={it.id}
                                    className="border-t border-[var(--rw-border)] cursor-pointer hover:bg-[var(--rw-surface-2)]"
                                    onClick={() => onClick(it)}
                                    data-testid={`${type}-grp-row-${it.id}`}
                                  >
                                    <td className="py-1.5 pr-3 font-semibold">
                                      {it.first_name} {it.last_name}
                                    </td>
                                    <td className="py-1.5 pr-3 font-mono-rw text-xs">
                                      {it.email || "—"}
                                    </td>
                                    <td className="py-1.5 pr-3 font-mono-rw text-xs">
                                      {it.phone || "—"}
                                    </td>
                                    <td className="py-1.5 pr-3 text-xs text-neutral-500 line-clamp-1">
                                      {it.notes || "—"}
                                    </td>
                                    <td className="py-1.5 pr-3 font-mono-rw text-xs">
                                      {it.last_contact_date
                                        ? formatLongDate(it.last_contact_date)
                                        : "—"}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {uncategorized.length > 0 && (
        <div
          className="bg-white border border-dashed border-[var(--rw-border)] rounded-[4px]"
          data-testid="grp-uncategorized-section"
        >
          <button
            type="button"
            onClick={() => toggleCat("__uncategorized__")}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--rw-surface-2)] text-left"
          >
            {openCats.has("__uncategorized__") ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )}
            <span className="w-2 h-2 rounded-full bg-neutral-400" />
            <span className="font-display text-base tracking-wide italic text-neutral-600">
              Uncategorized
            </span>
            <span className="text-[10px] uppercase tracking-[0.08em] text-neutral-500 ml-1">
              {uncategorized.length}{" "}
              {uncategorized.length === 1 ? "contact" : "contacts"} — click any row
              to assign a category
            </span>
          </button>
          {openCats.has("__uncategorized__") && (
            <div className="border-t border-[var(--rw-border)] px-4 py-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.08em] text-neutral-500 font-semibold">
                    <th className="py-1 pr-3">Name</th>
                    <th className="py-1 pr-3">Company</th>
                    <th className="py-1 pr-3">Email</th>
                    <th className="py-1 pr-3">Phone</th>
                    <th className="py-1 pr-3">Notes</th>
                    <th className="py-1 pr-3">Last Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {uncategorized
                    .slice()
                    .sort((a, b) =>
                      (a.last_name || a.name || "").localeCompare(
                        b.last_name || b.name || ""
                      )
                    )
                    .map((it) => (
                      <tr
                        key={it.id}
                        className="border-t border-[var(--rw-border)] cursor-pointer hover:bg-[var(--rw-surface-2)]"
                        onClick={() => onClick(it)}
                        data-testid={`${type}-uncat-row-${it.id}`}
                      >
                        <td className="py-1.5 pr-3 font-semibold">
                          {it.first_name || it.name} {it.last_name}
                        </td>
                        <td className="py-1.5 pr-3">{it.company || "—"}</td>
                        <td className="py-1.5 pr-3 font-mono-rw text-xs">
                          {it.email || "—"}
                        </td>
                        <td className="py-1.5 pr-3 font-mono-rw text-xs">
                          {it.phone || "—"}
                        </td>
                        <td className="py-1.5 pr-3 text-xs text-neutral-500 line-clamp-1">
                          {it.notes || "—"}
                        </td>
                        <td className="py-1.5 pr-3 font-mono-rw text-xs">
                          {it.last_contact_date
                            ? formatLongDate(it.last_contact_date)
                            : "—"}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {items.length === 0 && (
        <div className="bg-white border border-[var(--rw-border)] rounded-[4px] px-4 py-10 text-center text-neutral-400">
          No contacts yet.
        </div>
      )}
    </div>
  );
}
